import React, { useState, useEffect } from 'react';
import { X, Search, Loader2, Shield } from 'lucide-react';
import { usePanelMembers } from '../hooks/use-panels';

const PanelMembersSideSheet = ({ isOpen, onClose, panelId, panelName, memberCount }) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = usePanelMembers(panelId, debouncedSearch);

  if (!isOpen) return null;

  const renderAvatar = (user) => {
    if (user.avatarUrl) {
      return <img src={user.avatarUrl} alt={user.name} className="w-9 h-9 rounded-full object-cover" />;
    }
    const initials = (user.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    
    // Deterministic color
    const colors = ['bg-blue-100 text-blue-700', 'bg-purple-100 text-purple-700', 'bg-emerald-100 text-emerald-700', 'bg-orange-100 text-orange-700', 'bg-pink-100 text-pink-700', 'bg-teal-100 text-teal-700'];
    const colorIndex = (user.name || '').length % colors.length;
    
    return (
      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${colors[colorIndex]}`}>
        {initials}
      </div>
    );
  };

  const renderMember = (user, isLeadership = false) => (
    <div key={user.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer group">
      <div className="flex items-center gap-3">
        {renderAvatar(user)}
        <div>
          <div className="flex items-center gap-1.5">
            <h4 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
              {user.name}
            </h4>
            {isLeadership && (
              <Shield size={14} className="text-amber-500" />
            )}
          </div>
          <p className="text-xs text-gray-500">
            {user.specialization || (isLeadership ? 'Admin' : 'Member')}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 top-14 bg-black/20 z-[60] transition-opacity"
        onClick={onClose}
      />
      
      {/* Side Sheet */}
      <div className="fixed top-14 bottom-0 right-0 w-full md:w-[400px] bg-white shadow-2xl z-[60] transform transition-transform duration-300 flex flex-col border-l border-gray-200">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Panel Members</h2>
            <p className="text-sm text-gray-500 mt-0.5">{memberCount} members in {panelName}</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 -mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Search & Actions */}
          <div className="p-6 pb-2 space-y-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search members..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 border-transparent rounded-lg focus:bg-white focus:ring-2 focus:ring-[#6b46c1] focus:border-transparent transition-colors"
                />
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center p-12 text-gray-400 space-y-3">
              <Loader2 size={24} className="animate-spin text-[#6b46c1]" />
              <p className="text-sm font-medium">Loading members...</p>
            </div>
          ) : (
            <div className="p-4 space-y-6">
              {data?.leadership?.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 px-2">Leadership</h3>
                  <div className="space-y-1">
                    {data.leadership.map(u => renderMember(u, true))}
                  </div>
                </div>
              )}
              
              {data?.members?.length > 0 ? (
                <div>
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 px-2">All Members</h3>
                  <div className="space-y-1">
                    {data.members.map(u => renderMember(u, false))}
                  </div>
                </div>
              ) : (
                debouncedSearch && data?.leadership?.length === 0 && (
                  <div className="text-center p-8">
                    <p className="text-sm text-gray-500 font-medium">No members found matching "{debouncedSearch}"</p>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PanelMembersSideSheet;
